import React, { useEffect, useState } from 'react';
import {
  BudgetNoteSearchHighlightedText,
  renderSearchHighlightSegments,
} from '../budgetNote/BudgetNoteSearchHighlight';
import {
  buildBudgetSearchHighlightSegments,
  normalizeBudgetSearchQuery,
} from '../../utils/budgetNoteSearch';

function textIncludesQuery(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.toLowerCase());
}

export const SearchTextareaMirror: React.FC<{
  value: string;
  query: string;
  activeMatchId: string | null;
  buildMatchId: (start: number) => string;
  className: string;
  ariaLabel: string;
  onChange: (v: string) => void;
  onInput?: React.FormEventHandler<HTMLTextAreaElement>;
  onClick?: React.MouseEventHandler<HTMLTextAreaElement>;
  readOnly?: boolean;
  rows?: number;
  placeholder?: string;
  id?: string;
  disabled?: boolean;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
}> = ({
  value,
  query,
  activeMatchId,
  buildMatchId,
  className,
  ariaLabel,
  onChange,
  onInput,
  onClick,
  readOnly,
  rows,
  placeholder,
  id,
  disabled,
  textareaRef,
}) => {
  const q = normalizeBudgetSearchQuery(query);
  const showHighlight = Boolean(q) && textIncludesQuery(value, q);
  const segments = showHighlight
    ? buildBudgetSearchHighlightSegments(value, q, activeMatchId, buildMatchId)
    : null;
  const [editing, setEditing] = useState(false);
  const locked = readOnly || disabled;

  useEffect(() => {
    if (!showHighlight) setEditing(false);
  }, [showHighlight, q]);

  const showInlinePreview = Boolean(showHighlight && segments && (locked || !editing));

  const beginEditing = () => {
    if (locked) return;
    setEditing(true);
    requestAnimationFrame(() => textareaRef?.current?.focus());
  };

  return (
    <div
      className={
        'cp-budget-note-search-field-wrap' + (showInlinePreview ? ' has-inline-preview' : '')
      }
    >
      {showInlinePreview ? (
        <div
          className={className + ' cp-budget-note-search-inline-preview'}
          role="textbox"
          aria-label={ariaLabel}
          aria-readonly={locked || undefined}
          aria-disabled={disabled || undefined}
          tabIndex={locked ? -1 : 0}
          onMouseDown={(e) => {
            if (locked) return;
            e.preventDefault();
            beginEditing();
          }}
          onFocus={beginEditing}
        >
          {renderSearchHighlightSegments(segments!)}
        </div>
      ) : null}
      <textarea
        ref={textareaRef}
        id={id}
        className={
          className + (showInlinePreview ? ' cp-budget-note-search-inline-measurer' : '')
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onInput={onInput}
        onClick={onClick}
        onBlur={() => setEditing(false)}
        onFocus={() => setEditing(true)}
        readOnly={readOnly}
        rows={rows}
        placeholder={placeholder}
        aria-label={showInlinePreview ? undefined : ariaLabel}
        aria-hidden={showInlinePreview || undefined}
        tabIndex={showInlinePreview ? -1 : undefined}
        disabled={disabled}
      />
    </div>
  );
};

export { BudgetNoteSearchHighlightedText as SearchHighlightedText };
